import {useHistory} from 'modules/navigation/useRouter'
import React, {useContext, useEffect, useState} from 'react'
import useAsyncAction from "../../../modules/asyncCache/useAsyncAction";
import {
    auth_updateOrg_Api,
    user_getWorkingOrganizationInfo_Api,
    working_app_getWorkingOptions_Api
} from "../../../apis";
import {getResponseItem, getRoleLabel} from "../functions";
import {LoginContext} from "../../../components/LoginContext";
import {notifyOnError} from "../../AppLocals/factory/createErrorEvent";
import useTranslate from "../../../modules/local/useTranslate";
import OrganizationExplorerLoading from "../../DiscoveryGroupExplorer/components/OrganizationExplorerLoading";
import {successMessage} from "../../AppLocals/factory/createMessageNotification";
import {NotificationActionTypes} from "../../AppLocals/factory/createNotification";

const RELATIONSHIP_ROLE_LABELS = {
    SELF: 'student',
    FATHER: 'student (father)',
    MOTHER: 'student (mother)',
    BROTHER: 'student (sibling)',
    SISTER: 'student (sibling)',
    GUARDIAN: 'student (guardian)',
};

const ACTIVE_PROFILE_ID_KEY = '_wActiveProfileIdKey'
// Tổ chức/vai trò đang chọn (org switcher + role picker trong EducationPersonalPanel.js) trước đây
// CHỈ sống trong state của WorkingAppProvider, không lưu lại - mỗi lần chuyển tab bottom-nav
// (Trang chủ/Danh bạ khóa học/Trang cá nhân là các ROUTE khác nhau, tự mount lại 1
// WorkingAppProvider mới mỗi lần) là mất lựa chọn, activeOrganization rơi về login?.org mặc định,
// kéo theo activeProfileId (dù có lưu riêng) không khớp org nữa nên cũng chọn nhầm hồ sơ/vai trò
// khác. Lưu luôn object org đầy đủ (không chỉ id) vì cần title/avatar để hiển thị lại ngay từ lúc
// mount, chưa kịp gọi API.
const ACTIVE_ORGANIZATION_KEY = '_wActiveOrganizationKey'
const WorkingAppContext = React.createContext({})

export function getLocalActiveProfileId() {
    return localStorage.getItem(ACTIVE_PROFILE_ID_KEY) || null;
}

export function saveLocalActiveProfileId(value) {
    localStorage.setItem(ACTIVE_PROFILE_ID_KEY, value)
}

export function getLocalActiveOrganization() {
    try {
        const raw = localStorage.getItem(ACTIVE_ORGANIZATION_KEY)
        return raw ? JSON.parse(raw) : null
    } catch (e) {
        return null
    }
}

export function saveLocalActiveOrganization(org) {
    try {
        if (org?.id) {
            localStorage.setItem(ACTIVE_ORGANIZATION_KEY, JSON.stringify(org))
        }
    } catch (e) {
        // localStorage đầy/bị chặn (private mode...) - bỏ qua, không phải lỗi nghiêm trọng
    }
}


export const WorkingAppProvider = ({children, ...props}) => {
    const history = useHistory();
    const login = useContext(LoginContext)
    const t = useTranslate()
    const [refreshKey, setRefreshKey] = useState()
    const [init, setInit] = useState(false)
    const [isLoading, setLoading] = useState(true)
    const [workingAppState, setWorkingAppState] = useState({
        // Ưu tiên org đã lưu (owner vừa chọn trước đó) hơn login?.org mặc định - xem comment ở
        // ACTIVE_ORGANIZATION_KEY phía trên.
        activeOrganization: getLocalActiveOrganization() || login?.org || null,
        activeProfileId: getLocalActiveProfileId(),
        activeProfile: null,
        teacherOptions: [],
        studentOptions: [],
        courseOptions: [],
        courseClassOptions: [],

        organizationMemberOptions: [],
        departmentOptions: [],

        selectedClassId: null,
        selectedClass: null,
        selectedSubjectId: null,
        selectedSubject: null,

        selectedDepartmentId: null,
        selectedDepartment: null,

        latestOrgFeeds: [],
        orgOpeningCourses: [],
        groups: [],
        organizationInfo: null,
        refreshKey,
        setRefreshKey
    });

    // tiện: helper để update state một phần
    function updateWorkingAppState(patch) {
        setWorkingAppState(prev => {
            if (patch?.activeProfileId) {
                saveLocalActiveProfileId(patch?.activeProfileId)
            }
            if (patch?.activeOrganization?.id) {
                saveLocalActiveOrganization(patch.activeOrganization)
            }
            return ({...prev, ...patch})
        });

    }

    const {handleAsyncAction: updateWorkingOrg} = useAsyncAction({
        apiInfo: auth_updateOrg_Api,
        onSuccess: (result, data) => {
            successMessage(NotificationActionTypes.UPDATE, t)
            const responseItem = getResponseItem(data?.response);
            updateWorkingAppState({activeOrganization: responseItem?.org});
        },
        onError: notifyOnError(t),
    });

    const {isLoading: isOptionsLoading, handleAsyncAction: getWorkingOptions} = useAsyncAction({
        apiInfo: working_app_getWorkingOptions_Api,
        onError: () => {
            // API fail → vẫn unblock UI, hiện empty state thay vì loading mãi
            setInit(true)
            setLoading(false)
        },
        onSuccess: (__, data) => {
            const responseItem = getResponseItem(data?.response);
            const studentOptions = responseItem?.my_students || [];
            const teacherOptions = responseItem?.my_teachers || [];
            const organizationMemberOptions = responseItem?.my_org_members || [];
            const courseOptions = responseItem?.my_courses || [];

            let state = {
                studentOptions: [...studentOptions].sort((a, b) => (b.active_as_current ? 1 : 0) - (a.active_as_current ? 1 : 0))
                    .map(e => ({
                        ...e,
                        owner_title: e?.owner?.title,
                        patron_saint_name: e?.patron_saint_name,
                        fullname: e?.fullname,
                        idcode: e?.student_idcode,
                        avatar: e?.avatar,
                        relationship: e?.relationship || 'SELF',
                        role_title: RELATIONSHIP_ROLE_LABELS[e?.relationship] || 'student',
                    })),
                teacherOptions: [...teacherOptions].sort((a, b) => (b.active_as_current ? 1 : 0) - (a.active_as_current ? 1 : 0))
                    .map(e => ({
                        ...e,
                        owner_title: e?.owner?.title,
                        patron_saint_name: null,
                        fullname: e?.fullname,
                        idcode: e?.teacher_idcode,
                        avatar: e?.avatar,
                        role_title: "teacher",
                    })),
                organizationMemberOptions: [...organizationMemberOptions].sort((a, b) => (b.active_as_current ? 1 : 0) - (a.active_as_current ? 1 : 0))
                    .map(e => ({
                        ...e,
                        owner_title: e?.owner?.title,
                        patron_saint_name: e?.user?.patron_saint_name,
                        fullname: e?.user?.name,
                        idcode: null,
                        avatar: e?.owner?.avatar,
                        active_as_current: true,
                        _type: "organization_member",
                        role_title: getRoleLabel(e?.role_id),
                    })),
                courseOptions: [...courseOptions].sort((a, b) => (b.active_as_current ? 1 : 0) - (a.active_as_current ? 1 : 0)),
            };

            if (workingAppState?.activeOrganization?.id) {
                const filteredStudentOptions = state?.studentOptions.filter(
                    e => e?.owner?.id === workingAppState.activeOrganization?.id
                );
                const filteredTeacherOptions = state?.teacherOptions.filter(
                    e => e?.owner?.id === workingAppState.activeOrganization?.id
                );
                const filteredOrganizationMemberOptions = state?.organizationMemberOptions.filter(
                    e => e?.owner?.id === workingAppState.activeOrganization?.id
                );

                let nextProfile =
                    filteredStudentOptions?.filter(e => e?.active_as_current)
                        .find(e => e?.id === workingAppState.activeProfileId) ||
                    filteredTeacherOptions?.filter(e => e?.active_as_current)
                        .find(e => e?.id === workingAppState.activeProfileId) ||
                    filteredStudentOptions.find(e => e?.id === workingAppState.activeProfileId) ||
                    filteredTeacherOptions.find(e => e?.id === workingAppState.activeProfileId) ||
                    filteredOrganizationMemberOptions.find(e => e?.id === workingAppState.activeProfileId) ||
                    (
                        filteredOrganizationMemberOptions?.[0] ??
                        filteredStudentOptions?.[0] ??
                        filteredTeacherOptions?.[0] ??
                        null
                    ) || null;

                if (nextProfile && ((nextProfile?.id !== workingAppState.activeProfileId) || !workingAppState?.activeProfile)) {
                    saveLocalActiveProfileId(nextProfile.id);
                    state.activeProfile = nextProfile;
                    state.activeProfileId = nextProfile.id;
                }
            }

            updateWorkingAppState(state);
            setInit(true)
        },
    });

    const {isLoading: isWorkingOrganizationInfoLoading, handleAsyncAction: getWorkingOrganizationInfo} = useAsyncAction({
        apiInfo: user_getWorkingOrganizationInfo_Api,
        onSuccess: (__, data) => {
            const responseItem = getResponseItem(data?.response);
            const {opening_courses = [], latest_feeds = [], groups = [], my_classes = [], ...organizationInfo} = responseItem ?? {};

            updateWorkingAppState({
                latestOrgFeeds: latest_feeds,
                orgOpeningCourses: opening_courses,
                groups: groups,
                courseClassOptions: my_classes,
                organizationInfo
            });
            setLoading(false)
        },
        // activeOrganization đọc từ localStorage (org được chọn lần dùng trước, xem
        // ACTIVE_ORGANIZATION_KEY) có thể đã bị xoá/user không còn quyền truy cập -
        // get-auth-info trả lỗi "Organization not found" (OrganizationController@getOrganizationAuthInfo).
        // Trước đây không có onError nên app kẹt vĩnh viễn với 1 org đã chết (mỗi lần mount lại vẫn
        // đọc đúng id đó từ localStorage, không có gì tự sửa) - phải tự xoá cache cũ + rơi về
        // login?.org (org mặc định thật của user) để tự hồi phục, không bắt user tự xoá cache/đăng
        // xuất thủ công.
        onError: () => {
            const fallbackOrg = login?.org || null
            if (fallbackOrg?.id !== workingAppState.activeOrganization?.id) {
                localStorage.removeItem(ACTIVE_ORGANIZATION_KEY)
                updateWorkingAppState({activeOrganization: fallbackOrg})
            }
            setLoading(false)
        },
    });


    useEffect(() => {
        console.log('getWorkingOptions...')
        getWorkingOptions();
    }, []);

    useEffect(() => {
        if (init) {
            if (
                workingAppState.activeProfile?.owner?.id &&
                workingAppState.activeProfile?.owner?.id !== workingAppState.activeOrganization?.id
            ) {
                updateWorkingOrg({
                    organization_id: workingAppState.activeProfile.owner.id,
                    saved_type: 'default'
                });
            }
        }
    }, [init, workingAppState.activeProfile]);

    useEffect(() => {
        if (init) {
            if (!!!workingAppState.activeProfile) {
                const filteredStudentOptions = workingAppState?.studentOptions.filter(
                    e => e?.owner?.id === workingAppState.activeOrganization?.id
                );
                const filteredTeacherOptions = workingAppState?.teacherOptions.filter(
                    e => e?.owner?.id === workingAppState.activeOrganization?.id
                );
                const filteredOrganizationMemberOptions = workingAppState?.organizationMemberOptions.filter(
                    e => e?.owner?.id === workingAppState.activeOrganization?.id
                );

                let nextProfile =
                    filteredStudentOptions.find(e => e?.id === workingAppState.activeProfileId) ||
                    filteredTeacherOptions.find(e => e?.id === workingAppState.activeProfileId) ||
                    filteredOrganizationMemberOptions.find(e => e?.id === workingAppState.activeProfileId) ||
                    (
                        filteredOrganizationMemberOptions?.[0] ??
                        filteredStudentOptions?.[0] ??
                        filteredTeacherOptions?.[0] ??
                        null
                    ) || null;

                updateWorkingAppState({
                    activeProfile: nextProfile,
                    activeProfileId: nextProfile?.id,
                });
            }
        }

    }, [init, workingAppState.activeProfileId, refreshKey]);

    useEffect(() => {

        if (init) {
            if (workingAppState?.activeOrganization?.id) {
                getWorkingOrganizationInfo({}, {id: workingAppState.activeOrganization?.id});

                const filteredStudentOptions = workingAppState?.studentOptions.filter(
                    e => e?.owner?.id === workingAppState.activeOrganization?.id
                );
                const filteredTeacherOptions = workingAppState?.teacherOptions.filter(
                    e => e?.owner?.id === workingAppState.activeOrganization?.id
                );
                const filteredOrganizationMemberOptions = workingAppState?.organizationMemberOptions.filter(
                    e => e?.owner?.id === workingAppState.activeOrganization?.id
                );

                let nextProfile =
                    filteredStudentOptions.find(e => e?.id === workingAppState.activeProfileId) ||
                    filteredTeacherOptions.find(e => e?.id === workingAppState.activeProfileId) ||
                    filteredOrganizationMemberOptions.find(e => e?.id === workingAppState.activeProfileId) ||
                    (
                        filteredOrganizationMemberOptions?.[0] ??
                        filteredStudentOptions?.[0] ??
                        filteredTeacherOptions?.[0] ??
                        null
                    ) || null;

                if (!workingAppState?.activeProfile?.id
                    || (nextProfile?.id !== workingAppState.activeProfileId)
                ) {
                    updateWorkingAppState({
                        activeProfile: nextProfile,
                        activeProfileId: nextProfile?.id,
                    });
                }
            } else {
                // Không có org → không cần fetch org info, dừng loading
                setLoading(false)
            }
        }

    }, [workingAppState.activeOrganization?.id, init, refreshKey]);

    if (!init) return <OrganizationExplorerLoading style={{height: 420}}/>
    if (isOptionsLoading) return <OrganizationExplorerLoading style={{height: 420}}/>
    // if (isLoading) return <LoadingBlock style={{height: 420}} />

    return (
        <WorkingAppContext.Provider
            value={{
                ...props,
                ...workingAppState,
                updateWorkingAppState,
                isOptionsLoading,
                isLoading,
                updateWorkingOrg
            }}
        >
            {children}
        </WorkingAppContext.Provider>
    )
}

export default WorkingAppContext
